import { useEffect, useRef, useState } from 'react';
import { Pencil, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAppLogoUrl,
  getEventLogoUploadUrl,
  setEventLogo,
  deleteEventLogo,
} from '../../api/events';
import { uploadFileToS3 } from '../../api/photos';

const DEFAULT_LOGO_FALLBACK = '/logo.png';
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

export interface EventLogoProps {
  eventId: string;
  /** Resolved presigned URL of the event-specific logo (null if none). */
  eventLogoUrl: string | null;
  /** Show the hover edit overlay + clear button. Defaults to false (read-only). */
  editable?: boolean;
  /** Tailwind size class — defaults to `w-12 h-12`. */
  sizeClassName?: string;
  /** Used by tests to access the file input without clicking the overlay. */
  inputId?: string;
}

/**
 * Shows the event's logo. Falls back to the app-default logo (resolved from
 * `app_logo_s3_key`) and ultimately to a bundled brand asset. When `editable`,
 * hovering reveals an edit pencil that opens a file picker; the upload goes
 * directly to S3 via a presigned URL, then the s3Key is attached to the event.
 */
export function EventLogo({
  eventId,
  eventLogoUrl,
  editable = false,
  sizeClassName = 'w-12 h-12',
  inputId,
}: EventLogoProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Resolve the app-default logo lazily — only when no event-specific logo.
  const { data: appLogoUrl } = useQuery({
    queryKey: ['app-logo-url'],
    queryFn: getAppLogoUrl,
    enabled: !eventLogoUrl,
    staleTime: 60_000,
  });

  const [src, setSrc] = useState<string>(eventLogoUrl ?? appLogoUrl ?? DEFAULT_LOGO_FALLBACK);

  // Keep `src` in sync as the resolved URL changes.
  useEffect(() => {
    setSrc(eventLogoUrl ?? appLogoUrl ?? DEFAULT_LOGO_FALLBACK);
  }, [eventLogoUrl, appLogoUrl]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { uploadUrl, s3Key } = await getEventLogoUploadUrl(eventId, file.name, file.type);
      await uploadFileToS3(uploadUrl, file);
      return setEventLogo(eventId, s3Key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: () => {
      setError('Failed to upload logo. Try a different image.');
    },
  });

  const clear = useMutation({
    mutationFn: () => deleteEventLogo(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });

  function handlePick() {
    setError(null);
    fileInputRef.current?.click();
  }

  function handleFile(file: File | undefined) {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Use a JPG, PNG, WebP or SVG.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Logo must be smaller than 5 MB.');
      return;
    }
    upload.mutate(file);
  }

  const isLoading = upload.isPending || clear.isPending;

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <div className={`relative ${sizeClassName} flex-shrink-0`}>
        <img
          src={src}
          onError={() => {
            // If the resolved URL fails (expired? misconfigured?), fall back.
            if (src !== DEFAULT_LOGO_FALLBACK) setSrc(DEFAULT_LOGO_FALLBACK);
          }}
          alt="Event logo"
          className={`${sizeClassName} rounded-lg object-cover border border-gray-200 bg-white`}
        />
        {editable && (
          <>
            <button
              type="button"
              onClick={handlePick}
              disabled={isLoading}
              aria-label="Change event logo"
              className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 hover:bg-black/40 transition-colors group"
            >
              {isLoading ? (
                <Loader2 size={18} className="text-white animate-spin" />
              ) : (
                <Pencil
                  size={16}
                  className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                />
              )}
            </button>
            <input
              ref={fileInputRef}
              id={inputId}
              type="file"
              accept={ALLOWED_TYPES.join(',')}
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="event-logo-file-input"
            />
          </>
        )}
      </div>
      {editable && eventLogoUrl && !isLoading && (
        <button
          type="button"
          onClick={() => clear.mutate()}
          className="text-xs text-gray-500 hover:text-red-600"
        >
          Remove logo
        </button>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
